import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import OutsideClickHandler from "react-outside-click-handler";
import {
  LuCheck,
  LuChevronDown,
  LuChevronRight,
  LuCopy,
  LuMic,
  LuMoreHorizontal,
  LuPencil,
  LuPlus,
  LuThumbsDown,
  LuThumbsUp,
  LuTrash2,
} from "react-icons/lu";
import { handleError } from "src/Common/function";
import AiService from "src/Services/API/ai.service";
import { AiBlockList, MarkdownLite } from "./AiBlocks";

// "Ask Makani AI" — conversational analytics over business data. Streams answers
// from the AI service (SSE) and renders the structured AiResponse blocks.
// API reference: makani-ai-service/docs/ASK_MAKANI_AI_API.md

// Header (60px) + report-center tab bar (53px).
const PAGE_HEIGHT = "h-[calc(100vh-113px)]";

const starPath = (cx, cy, r) =>
  `M ${cx} ${cy - r} Q ${cx + r * 0.12} ${cy - r * 0.12} ${cx + r} ${cy} Q ${cx + r * 0.12} ${cy + r * 0.12} ${cx} ${cy + r} Q ${cx - r * 0.12} ${cy + r * 0.12} ${cx - r} ${cy} Q ${cx - r * 0.12} ${cy - r * 0.12} ${cx} ${cy - r} Z`;

const SparklesLogo = ({ size = 28 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44" fill="none">
    <path
      d="M16 6L21 17L32 22L21 27L16 38L11 27L0 22L11 17L16 6ZM16 15.66L14 20L9.66 22L14 24L16 28.34L18 24L22.34 22L18 20L16 15.66ZM36 16L33.48 10.52L28 8L33.48 5.5L36 0L38.5 5.5L44 8L38.5 10.52L36 16ZM36 44L33.48 38.52L28 36L33.48 33.5L36 28L38.5 33.5L44 36L38.5 38.52L36 44Z"
      fill="#0065FD"
    />
  </svg>
);

const PanelToggleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M9 1.5H3C2.17157 1.5 1.5 2.17157 1.5 3V9C1.5 9.82843 2.17157 10.5 3 10.5H9L9 1.5ZM10.5 1.5H11C11.8284 1.5 12.5 2.17157 12.5 3V9C12.5 9.82843 11.8284 10.5 11 10.5H10.5L10.5 1.5ZM14 3C14 1.34315 12.6569 0 11 0H3C1.34315 0 -9.53674e-07 1.34315 -9.53674e-07 3V9C-9.53674e-07 10.6569 1.34315 12 3 12H11C12.6569 12 14 10.6569 14 9V3Z"
      fill="#7C829A"
    />
  </svg>
);

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path
      d="M4.85087 0.112057C5.04726 -0.0481488 5.33708 -0.0365068 5.52014 0.146562L10.1868 4.81323C10.3821 5.00849 10.3821 5.325 10.1868 5.52026C9.99155 5.71552 9.67504 5.71552 9.47978 5.52026L5.16663 1.20711L0.853478 5.52026C0.658216 5.71552 0.341709 5.71552 0.146447 5.52026C-0.0488155 5.325 -0.0488155 5.00849 0.146447 4.81323L4.81311 0.146562L4.85087 0.112057Z"
      fill="white"
    />
    <path
      d="M4.66663 9.83341V0.500078C4.66663 0.223935 4.89049 7.78834e-05 5.16663 7.78834e-05C5.44277 7.78834e-05 5.66663 0.223935 5.66663 0.500078V9.83341C5.66663 10.1096 5.44277 10.3334 5.16663 10.3334C4.89049 10.3334 4.66663 10.1096 4.66663 9.83341Z"
      fill="white"
    />
  </svg>
);

const CsvFileIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#16A34A"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v5h5" />
    <path d="M8.5 13.5h7M8.5 16.5h7" />
  </svg>
);

const PdfFileIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#DC2626"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v5h5" />
    <path d="M9 17v-4h1.4a1.3 1.3 0 1 1 0 2.6H9" />
  </svg>
);

const relativeTime = (iso) => {
  const parsed = moment(iso);
  if (!parsed.isValid()) return "";
  return moment().diff(parsed, "seconds") < 60 ? "Just now" : parsed.fromNow();
};

// Browser speech-to-text (Web Speech API). Chrome/Edge/Safari expose it as
// webkitSpeechRecognition; Firefox doesn't support it, so the mic button shows
// a notice there. Requires HTTPS or localhost.
const SpeechRecognitionImpl =
  typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : undefined;

// Defined outside the page component so re-renders don't remount the textarea
// (which would drop focus while typing).
const StopIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
    <rect x="2" y="2" width="10" height="10" rx="2" />
  </svg>
);

const Composer = ({ value, onChange, onSend, placeholder, disabled, onStop }) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  // Snapshot of the input when dictation starts, so interim results can be
  // re-rendered live without duplicating already-typed text.
  const baseValueRef = useRef("");
  const valueRef = useRef(value);
  valueRef.current = value;

  // Stop dictation if the composer unmounts mid-recording.
  useEffect(
    () => () => {
      recognitionRef.current?.abort?.();
    },
    [],
  );

  const stopListening = () => {
    recognitionRef.current?.stop?.();
    setListening(false);
  };

  const startListening = () => {
    if (!SpeechRecognitionImpl) {
      toast.info("Speech recognition is not supported in this browser");
      return;
    }
    const recognition = new SpeechRecognitionImpl();
    recognitionRef.current = recognition;
    recognition.lang = navigator.language || "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;

    baseValueRef.current = valueRef.current ? `${valueRef.current.trimEnd()} ` : "";

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      onChange((baseValueRef.current + transcript).slice(0, 4000));
    };
    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        toast.error("Microphone access was denied");
      }
      setListening(false);
    };
    recognition.onend = () => setListening(false);

    try {
      recognition.start();
      setListening(true);
    } catch (error) {
      setListening(false);
    }
  };

  // Always stop dictation before sending — otherwise the still-running
  // recognition would keep writing the transcript back into the cleared input.
  const handleSend = () => {
    if (listening) stopListening();
    onSend();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="overflow-hidden rounded-[16px] border border-[#EEF0F9] bg-white shadow-[0_15px_30px_-4px_rgba(16,24,40,0.03)]">
      <div className="px-6 pb-4 pt-6">
        <textarea
          rows={2}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={4000}
          className="w-full resize-none bg-transparent font-OpenSansRegular text-[18px] leading-7 text-[#1A2332] outline-none placeholder:text-[#868DA6]"
        />
      </div>
      <div className="flex items-center justify-between px-4 pb-4">
        {/* Attachment button — visual only, the reports AI API takes text questions. */}
        <button
          type="button"
          className="flex h-[40px] w-[40px] items-center justify-center rounded-full text-[#7C829A] hover:bg-[#F4F6FB]"
        >
          <LuPlus size={20} />
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={listening ? stopListening : startListening}
            title={listening ? "Stop dictation" : "Dictate your question"}
            className={`flex h-[40px] w-[40px] items-center justify-center rounded-[8px] border shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-colors ${
              listening
                ? "animate-pulse border-[#EF4444] bg-[#FEF2F2] text-[#EF4444]"
                : "border-[#C9CEE2] bg-white text-[#1A2332] hover:bg-[#F4F6FB]"
            }`}
          >
            <LuMic size={16} />
          </button>
          {disabled ? (
            // Answer is streaming — offer Stop instead of a dead Send button.
            <button
              type="button"
              onClick={onStop}
              className="flex h-[40px] items-center gap-2 rounded-[8px] bg-[#1A2332] px-4 font-OpenSansSemiBold text-[16px] capitalize leading-6 text-white hover:opacity-90"
            >
              <StopIcon />
              Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!value.trim()}
              className="flex h-[40px] items-center gap-2 rounded-[8px] bg-blueColor px-4 font-OpenSansSemiBold text-[16px] capitalize leading-6 text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <SendIcon />
              Send
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const UserMessage = ({ message }) => (
  <div className="flex flex-col items-end">
    <div className="max-w-[576px] rounded-[18px] rounded-br-[4px] bg-[#DAEBFF] px-6 py-4 font-OpenSansRegular text-[16px] leading-6 text-[#1A2332] shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
      {message.content}
    </div>
    <span className="mt-2 font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6]">
      {moment(message.createdAt).isValid() ? moment(message.createdAt).format("h:mm A") : ""}
    </span>
  </div>
);

const AssistantMessage = ({
  message,
  feedback,
  onFeedback,
  onCopy,
  copied,
  onExport,
  exporting,
  onSuggestion,
  isLast,
}) => {
  const payload = message.payload;
  const suggestions = isLast ? payload?.suggestions || [] : [];

  return (
    <div className="flex w-full max-w-[896px] flex-col items-start gap-6">
      <div className="flex items-center gap-3">
        <SparklesLogo size={28} />
        <span className="font-OpenSansSemiBold text-[14px] capitalize leading-5 tracking-[0.14px] text-[#1A2332]">
          Makani AI
        </span>
        <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6]">
          {message.streaming ? "Just now" : relativeTime(message.createdAt)}
        </span>
      </div>

      <div className="w-full rounded-[18px] border border-[#E5E8F3] bg-white p-6 shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
        {message.error ? (
          <p className="font-OpenSansRegular text-[14px] leading-6 text-[#EF4444]">{message.error}</p>
        ) : message.streaming && !message.content ? (
          <div className="flex items-center gap-1.5 py-1">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className="h-[7px] w-[7px] animate-bounce rounded-full bg-[#989BA0]"
                style={{ animationDelay: `${dot * 0.15}s` }}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <MarkdownLite
              text={message.content}
              className="font-OpenSansSemiBold text-[16px] leading-6 text-[#333333]"
            />
            {!message.streaming && payload?.blocks?.length > 0 && <AiBlockList blocks={payload.blocks} />}
            {!message.streaming && payload?.dataSources?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-OpenSansRegular text-[12px] leading-[18px] text-[#989BA0]">Data sources:</span>
                {payload.dataSources.map((source, index) => (
                  <span
                    key={index}
                    className="rounded-full bg-[#F1F2F7] px-3 py-1 font-OpenSansRegular text-[12px] leading-[18px] text-[#7C829A]"
                  >
                    {source}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!message.streaming && !message.error && (
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onFeedback(message.id, "UP")}
              className={`flex h-[32px] w-[32px] items-center justify-center rounded-[6px] hover:bg-[#EFF1F7] ${
                feedback === "UP" ? "text-[#0065FD]" : "text-[#7C829A]"
              }`}
            >
              <LuThumbsUp size={16} />
            </button>
            <button
              type="button"
              onClick={() => onFeedback(message.id, "DOWN")}
              className={`flex h-[32px] w-[32px] items-center justify-center rounded-[6px] hover:bg-[#EFF1F7] ${
                feedback === "DOWN" ? "text-[#EF4444]" : "text-[#7C829A]"
              }`}
            >
              <LuThumbsDown size={16} />
            </button>
            <span className="h-[16px] w-[1px] bg-[#E5E8F3]" />
            <button
              type="button"
              onClick={() => onCopy(message)}
              className="flex h-[32px] items-center gap-2 rounded-[6px] px-1 font-OpenSansSemiBold text-[14px] capitalize leading-5 tracking-[0.14px] text-[#7C829A] hover:bg-[#EFF1F7]"
            >
              {copied ? <LuCheck size={16} className="text-[#16A34A]" /> : <LuCopy size={16} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {payload?.exportable && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onExport(message.id, "csv")}
                disabled={exporting === `${message.id}-csv`}
                className="flex items-center gap-2 rounded-[8px] border border-[#EDF2F7] bg-white px-3 py-1.5 font-OpenSansSemiBold text-[12px] leading-4 text-[#333333] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:bg-[#F8FAFC] disabled:opacity-60"
              >
                <CsvFileIcon />
                {exporting === `${message.id}-csv` ? "Exporting..." : "Export CSV"}
              </button>
              <button
                type="button"
                onClick={() => onExport(message.id, "pdf")}
                disabled={exporting === `${message.id}-pdf`}
                className="flex items-center gap-2 rounded-[8px] border border-[#EDF2F7] bg-white px-3 py-1.5 font-OpenSansSemiBold text-[12px] leading-4 text-[#333333] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:bg-[#F8FAFC] disabled:opacity-60"
              >
                <PdfFileIcon />
                Export PDF
              </button>
            </div>
          )}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => onSuggestion(suggestion)}
              className="rounded-full border border-[#E5E8F3] bg-white px-5 py-2.5 font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px] text-[#1A2332] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#DAEBFF]"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AskMakaniAI = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [groups, setGroups] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [prompts, setPrompts] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const scrollRef = useRef(null);
  const copyTimerRef = useRef(null);
  // Controller for the in-flight streaming request, so the user can stop it.
  const abortRef = useRef(null);

  const refreshConversations = useCallback(() => {
    AiService.getConversations()
      .then((data) => setGroups(Array.isArray(data) ? data : []))
      .catch(() => {}); // sidebar is non-critical; the chat still works without it
  }, []);

  useEffect(() => {
    refreshConversations();
    AiService.getSuggestedPrompts()
      .then((data) => setPrompts(Array.isArray(data) ? data : []))
      .catch(() => {});
    return () => clearTimeout(copyTimerRef.current);
  }, [refreshConversations]);

  // Keep the newest message in view while streaming.
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [thread]);

  const isGroupOpen = (label) => openGroups[label] ?? label === "Today";

  const startNewChat = () => {
    setConversationId(null);
    setThread([]);
    setInput("");
  };

  const openConversation = (id) => {
    if (id === conversationId || sending) return;
    setConversationId(id);
    setThread([]);
    setLoadingThread(true);
    AiService.getConversationMessages(id)
      .then((messages) =>
        setThread(
          (messages || []).map((message) => ({
            id: message.id,
            role: message.role,
            content: message.content,
            payload: message.payload,
            createdAt: message.createdAt,
          })),
        ),
      )
      .catch((error) => handleError(error))
      .finally(() => setLoadingThread(false));
  };

  const sendMessage = async (raw) => {
    const message = (raw ?? input).trim();
    if (!message || sending) return;

    setInput("");
    setSending(true);

    const now = moment().toISOString();
    const placeholderId = `pending-${Date.now()}`;
    setThread((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: "USER", content: message, createdAt: now },
      { id: placeholderId, role: "ASSISTANT", content: "", createdAt: now, streaming: true },
    ]);

    const patchPlaceholder = (patch) =>
      setThread((prev) => prev.map((item) => (item.id === placeholderId ? { ...item, ...patch } : item)));

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await AiService.streamChat(
        { conversationId, message },
        {
          onToken: (token) =>
            setThread((prev) =>
              prev.map((item) => (item.id === placeholderId ? { ...item, content: item.content + token } : item)),
            ),
          onResult: (result) => {
            setConversationId(result.conversationId);
            patchPlaceholder({
              id: result.messageId || placeholderId,
              content: result.message,
              payload: result,
              streaming: false,
              createdAt: result.timestamp ? moment(result.timestamp).toISOString() : now,
            });
            refreshConversations();
          },
          onError: (error) => patchPlaceholder({ streaming: false, error: error.message }),
        },
        controller.signal,
      );
    } catch (error) {
      if (error?.name === "AbortError") {
        // User pressed Stop — keep whatever partial text streamed in.
        setThread((prev) =>
          prev.map((item) =>
            item.id === placeholderId
              ? { ...item, streaming: false, content: item.content || "Response was stopped." }
              : item,
          ),
        );
      } else {
        patchPlaceholder({ streaming: false, error: error?.message || "The AI request failed" });
      }
    } finally {
      abortRef.current = null;
      setSending(false);
    }
  };

  const stopStreaming = () => abortRef.current?.abort();

  const handleFeedback = async (messageId, rating) => {
    const previous = feedback[messageId];
    if (previous === rating) return;
    setFeedback((prev) => ({ ...prev, [messageId]: rating }));
    try {
      await AiService.sendFeedback(messageId, rating);
    } catch (error) {
      setFeedback((prev) => ({ ...prev, [messageId]: previous }));
      handleError(error);
    }
  };

  const handleCopy = (message) => {
    navigator.clipboard
      .writeText(message.content || "")
      .then(() => {
        setCopiedId(message.id);
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(() => toast.error("Could not copy to clipboard"));
  };

  const handleExport = async (messageId, format) => {
    try {
      setExporting(`${messageId}-${format}`);
      const file = await AiService.exportMessage(messageId, format);
      if (!file) {
        toast.info("This answer has no table to export");
        return;
      }
      const url = window.URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error?.status === 501) toast.info("PDF export is coming soon");
      else handleError(error);
    } finally {
      setExporting(null);
    }
  };

  const startRename = (item) => {
    setMenuFor(null);
    setRenamingId(item.id);
    setRenameValue(item.title || "");
  };

  const submitRename = async () => {
    const title = renameValue.trim();
    const id = renamingId;
    setRenamingId(null);
    if (!id || !title) return;
    try {
      await AiService.renameConversation(id, title);
      refreshConversations();
    } catch (error) {
      handleError(error);
    }
  };

  const handleDelete = async (id) => {
    setMenuFor(null);
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await AiService.deleteConversation(id);
      if (id === conversationId) startNewChat();
      refreshConversations();
    } catch (error) {
      handleError(error);
    }
  };

  const lastAssistantIndex = (() => {
    for (let i = thread.length - 1; i >= 0; i--) {
      if (thread[i].role === "ASSISTANT") return i;
    }
    return -1;
  })();

  const hasThread = thread.length > 0 || loadingThread;

  const composer = (
    <Composer
      value={input}
      onChange={setInput}
      onSend={() => sendMessage()}
      placeholder={hasThread ? "Ask a follow-up question..." : "Ask anything about your business..."}
      disabled={sending}
      onStop={stopStreaming}
    />
  );

  return (
    <div className={`flex ${PAGE_HEIGHT} bg-[#F8F9FE]`}>
      {/* Sidebar — chat history. The container animates its width; the expanded
          content keeps a fixed 256px inner width (so text doesn't reflow while
          sliding) and cross-fades with the collapsed icon rail. */}
      <div
        className={`relative shrink-0 overflow-hidden border-r border-[#E5E8F3] bg-white transition-[width] duration-300 ease-in-out ${
          sidebarOpen ? "w-[256px]" : "w-[68px]"
        }`}
      >
        <div
          className={`flex h-full w-[256px] flex-col transition-opacity duration-200 ${
            sidebarOpen ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <div className="flex items-center justify-between px-4 pt-6">
            <button
              type="button"
              onClick={startNewChat}
              className="flex h-[32px] items-center gap-2 rounded-[8px] border border-[#C9CEE2] bg-white px-3 font-OpenSansSemiBold text-[14px] capitalize leading-5 tracking-[0.14px] text-[#1A2332] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#F4F6FB]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path
                  d="M9.83333 4.66667C10.1095 4.66667 10.3333 4.89052 10.3333 5.16667C10.3333 5.44281 10.1095 5.66667 9.83333 5.66667H0.5C0.223858 5.66667 0 5.44281 0 5.16667C0 4.89052 0.223858 4.66667 0.5 4.66667H9.83333Z"
                  fill="#1A2332"
                />
                <path
                  d="M4.66667 9.83333V0.5C4.66667 0.223858 4.89052 0 5.16667 0C5.44281 0 5.66667 0.223858 5.66667 0.5V9.83333C5.66667 10.1095 5.44281 10.3333 5.16667 10.3333C4.89052 10.3333 4.66667 10.1095 4.66667 9.83333Z"
                  fill="#1A2332"
                />
              </svg>
              New chat
            </button>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="flex h-[36px] w-[36px] items-center justify-center rounded-[12px] text-[#7C829A] hover:bg-[#F4F6FB]"
            >
              <PanelToggleIcon />
            </button>
          </div>

          <div className="px-4 pb-1 pt-6 font-OpenSansSemiBold text-[14px] capitalize leading-5 tracking-[0.14px] text-[#1A2332]">
            Chat history
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-6">
            {groups.length === 0 && (
              <div className="px-3 py-4 font-OpenSansRegular text-[13px] leading-5 text-[#7C829A]">
                No conversations yet
              </div>
            )}
            <div className="flex flex-col gap-4">
              {groups.map((group) => (
                <div key={group.label} className="flex flex-col gap-[5px]">
                  <button
                    type="button"
                    onClick={() => setOpenGroups((prev) => ({ ...prev, [group.label]: !isGroupOpen(group.label) }))}
                    className="flex w-full items-center justify-between rounded-[8px] px-3 py-2 font-OpenSansSemiBold text-[12px] uppercase leading-[18px] tracking-[0.12px] text-[#7C829A] hover:bg-[#F4F6FB]"
                  >
                    {group.label}
                    {isGroupOpen(group.label) ? <LuChevronDown size={16} /> : <LuChevronRight size={16} />}
                  </button>
                  {isGroupOpen(group.label) &&
                    (group.items || []).map((item) => (
                      <div
                        key={item.id}
                        className={`group relative flex min-h-[36px] items-center rounded-[8px] ${
                          item.id === conversationId ? "bg-[#EFF6FF]" : "hover:bg-[#F4F6FB]"
                        }`}
                      >
                        {renamingId === item.id ? (
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(event) => setRenameValue(event.target.value)}
                            onBlur={submitRename}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") submitRename();
                              if (event.key === "Escape") setRenamingId(null);
                            }}
                            maxLength={200}
                            className="mx-2 my-1.5 w-full rounded-[6px] border border-[#0065FD] px-2 py-1 font-OpenSansRegular text-[14px] leading-5 text-[#1A2332] outline-none"
                          />
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => openConversation(item.id)}
                              className="min-w-0 flex-1 truncate p-3 text-left font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px] text-[#1A2332]"
                              title={item.title}
                            >
                              {item.title}
                            </button>
                            <button
                              type="button"
                              onClick={() => setMenuFor(menuFor === item.id ? null : item.id)}
                              className="mr-2 flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[6px] text-[#7C829A] hover:bg-[#E9EDF7]"
                            >
                              <LuMoreHorizontal size={16} />
                            </button>
                            {menuFor === item.id && (
                              <OutsideClickHandler onOutsideClick={() => setMenuFor(null)}>
                                <div className="absolute right-1 top-[38px] z-20 w-[150px] rounded-[10px] border border-[#E5E8F3] bg-white py-1 shadow-[0_8px_24px_rgba(16,24,40,0.12)]">
                                  <button
                                    type="button"
                                    onClick={() => startRename(item)}
                                    className="flex w-full items-center gap-2 px-3 py-2 font-OpenSansRegular text-[14px] leading-5 text-[#1A2332] hover:bg-[#F4F6FB]"
                                  >
                                    <LuPencil size={14} />
                                    Rename
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(item.id)}
                                    className="flex w-full items-center gap-2 px-3 py-2 font-OpenSansRegular text-[14px] leading-5 text-[#EF4444] hover:bg-[#FEF2F2]"
                                  >
                                    <LuTrash2 size={14} />
                                    Delete
                                  </button>
                                </div>
                              </OutsideClickHandler>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Collapsed icon rail */}
        <div
          className={`absolute inset-y-0 left-0 flex w-[68px] flex-col items-center gap-4 px-4 pt-6 transition-opacity duration-200 ${
            sidebarOpen ? "pointer-events-none opacity-0" : "opacity-100"
          }`}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex h-[36px] w-[36px] items-center justify-center rounded-[12px] text-[#7C829A] hover:bg-[#F4F6FB]"
          >
            <PanelToggleIcon />
          </button>
          <button
            type="button"
            onClick={startNewChat}
            className="flex h-[32px] w-[32px] items-center justify-center rounded-[8px] border border-[#C9CEE2] bg-white text-[#1A2332] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#F4F6FB]"
          >
            <LuPlus size={16} />
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex min-w-0 flex-1 flex-col">
        {!hasThread ? (
          // Welcome screen
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto flex max-w-[944px] flex-col items-center gap-12 px-14 py-16">
              <div className="flex flex-col items-center gap-6">
                <SparklesLogo size={48} />
                <div className="flex flex-col items-center gap-3">
                  <h1 className="text-center font-OpenSansSemiBold text-[30px] leading-9 text-[#101828]">Makani AI</h1>
                  <p className="max-w-[605px] text-center font-OpenSansRegular text-[16px] leading-6 text-[#7C829A]">
                    Ask questions about your business operations, technicians, purchase orders, service tickets,
                    customers, and reports.
                  </p>
                </div>
              </div>

              <div className="w-full max-w-[832px]">{composer}</div>

              {prompts.length > 0 && (
                <div className="flex w-full max-w-[832px] flex-wrap gap-4">
                  {prompts.map((prompt) => (
                    <button
                      key={prompt.id}
                      type="button"
                      onClick={() => sendMessage(prompt.question)}
                      className="flex h-[96px] w-[265px] min-w-[200px] flex-col gap-1 rounded-[8px] bg-white/80 p-4 text-left transition-colors hover:bg-[#DAEBFF] hover:shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
                    >
                      <div className="font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px] text-[#1A2332]">
                        {prompt.question}
                      </div>
                      <div className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
                        {prompt.category}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          // Conversation view
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto">
              <div className="mx-auto flex max-w-[960px] flex-col gap-8 p-8">
                {loadingThread ? (
                  <div className="flex items-center justify-center py-16 font-OpenSansRegular text-[14px] text-[#868DA6]">
                    Loading conversation...
                  </div>
                ) : (
                  thread.map((message, index) =>
                    message.role === "USER" ? (
                      <UserMessage key={message.id} message={message} />
                    ) : (
                      <AssistantMessage
                        key={message.id}
                        message={message}
                        feedback={feedback[message.id]}
                        onFeedback={handleFeedback}
                        onCopy={handleCopy}
                        copied={copiedId === message.id}
                        onExport={handleExport}
                        exporting={exporting}
                        onSuggestion={(text) => sendMessage(text)}
                        isLast={index === lastAssistantIndex && !sending}
                      />
                    ),
                  )
                )}
              </div>
            </div>
            <div className="px-8 pb-6 pt-2">
              <div className="mx-auto max-w-[896px]">{composer}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AskMakaniAI;
